<template>
    <AddFileSection
        :id="AddFileFormSectionIds.DOCUMENTS"
        :sectionDescription="
            hasDocuments
                ? 'Add or remove documents to include in this file. Select clients and toggle on eSign for signatures.'
                : ''
        "
        :sectionHeading="hasDocuments ? 'Send documents' : ''"
    >
        <SendDocumentsCardButton
            v-if="!addFileStore.branch?.id"
            disabled
        />
        <div
            v-else
            :class="{ 'mt-2': hasDocuments }"
        >
            <MultiFileDocumentUpload
                active
                :allowTemplateSelection="false"
                allowDocumentSelection
                :branchId="addFileStore.branch.id"
                :clients="clientOptions"
                :dsUploadSize="hasDocuments ? 'md' : 'collapsed'"
                :enableNotificationFlag="false"
                headerText="Added documents"
                headerVariant="plain"
                invertedForm
                :routes="formRoutes"
                :showFirstLevelErrorBanner="false"
                @addClient="scrollToClientsSection"
            >
                <template
                    v-if="!hasDocuments"
                    #clickTrigger="{ triggerFilePicker }"
                >
                    <SendDocumentsCardButton @addDocuments="triggerFilePicker" />
                </template>
            </MultiFileDocumentUpload>
        </div>

        <DsDialog
            v-model:open="isEsignInvalidationDialogOpen"
            primaryLabel="Update eSignature documents"
            secondaryLabel="Close"
            title="eSignature document update is required"
            @clickPrimary="handleInvalidationDialogConfirm"
            @clickSecondary="isEsignInvalidationDialogOpen = false"
        >
            <p class="text-content-lg">
                Updating this client's information requires you to redo the eSignature flow. The affected documents have been
                highlighted so you can quickly restart them.
            </p>
        </DsDialog>
    </AddFileSection>
</template>

<script setup lang="ts">
import type { ServerErrors } from '@/company-portal/add-file/add-file-form-errors.types';
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import type { TPSDocument } from '@/company-portal/add-file/add-file.types';
import { AddFileFormSectionIds } from '@/company-portal/add-file/add-file.types';
import { useAddFileFormScroller } from '@/company-portal/add-file/use-add-file-form-scroller';
import DsDialog from '@/components/design-system/DsDialog.vue';
import AddFileSection from '@/components/Views/Company/AddFile/AddFileSection.vue';
import SendDocumentsCardButton from '@/components/Views/Company/AddFile/SendDocumentsCardButton.vue';
import type { ClientOptionInput } from '@/components/Views/Company/File/Documents/document-upload-utils';
import { buildAddTpsDocumentParams, mapDocumentErrors } from '@/components/Views/Company/File/Documents/document-upload-utils';
import MultiFileDocumentUpload from '@/components/Views/Company/File/Documents/MultiFileDocumentUpload.vue';
import type { AddFileFormDocumentRoutes } from '@/components/Views/Company/File/file.types';
import { type DocumentUploadErrorResponse } from '@/components/Views/Company/File/file.types';
import { ESIGN_INVALIDATION_DIALOG_INJECTION_KEY } from '@/injection-keys';
import { scrollToElement } from '@/shared/utils/browser';
import { useFileClientStore } from '@/stores/company/file-client.store';
import { useFileDocumentStore } from '@/stores/file-document.store';
import { watchImmediate } from '@vueuse/core';
import { computed, provide, ref, watch } from 'vue';

const props = withDefaults(
    defineProps<{
        clientSectionId?: string;
        routes: {
            esign_edit_url: string; // i.e.: /user/home/document/eSign/edit
            esign_edit_url_vendor: string; // i.e.: /user/vendor/order/esign/edit
        };
    }>(),
    {
        clientSectionId: 'clients-section',
    },
);

const fileDocumentStore = useFileDocumentStore();
const addFileStore = useAddFileStore();
const fileClientStore = useFileClientStore();

const { scrollToDocumentSection } = useAddFileFormScroller();

// Shared, section-level dialog raised when editing a client invalidates a document's prepared
// eSignature. Each affected document card opens it via the provided handler below.
const isEsignInvalidationDialogOpen = ref(false);
provide(ESIGN_INVALIDATION_DIALOG_INJECTION_KEY, {
    openEsignInvalidationDialog: () => (isEsignInvalidationDialogOpen.value = true),
    closeEsignInvalidationDialog: () => (isEsignInvalidationDialogOpen.value = false),
});

const handleInvalidationDialogConfirm = () => {
    isEsignInvalidationDialogOpen.value = false;
    scrollToDocumentSection();
};

const formRoutes = computed<AddFileFormDocumentRoutes>(() => {
    return {
        esign_edit: addFileStore.tpsOrder ? props.routes.esign_edit_url_vendor : props.routes.esign_edit_url,
    };
});

const clientOptions = computed<ClientOptionInput[]>(() =>
    fileClientStore.availableClientOptions.map(client => ({
        id: client.id,
        value: client.id,
        email: client.email,
        first: client.first,
        last: client.last,
        role: client.role as ClientOptionInput['role'], // assertion: the role types are incompatible between this and the file details client type
    })),
);
const hasDocuments = computed(() => fileDocumentStore.documentConfigs.size > 0);

const scrollToClientsSection = () => {
    requestAnimationFrame(() => {
        scrollToElement(document.getElementById(props.clientSectionId));
    });
};

watch(
    () => addFileStore.submissionErrors,
    errors => {
        if (!errors) return;
        // assertion: the add-file form's ServerErrorResponse and DocumentUploadErrorResponse
        // share the same structural shape (`{ errors?, message? }`) at this boundary
        const documentErrors = errors as DocumentUploadErrorResponse;
        Array.from(fileDocumentStore.documentConfigs.values()).forEach(config => {
            fileDocumentStore.updateEsignStateInvalidationError(config.documentId, '');
            fileDocumentStore.updateDocumentErrors(config.documentId, mapDocumentErrors(documentErrors, config.formKey));
        });
    },
);

watch(
    () => fileDocumentStore.esignEditError,
    errors => {
        if (!errors) return;

        addFileStore.submissionErrors = {
            message: errors.message,
            errors: errors.errors as ServerErrors, // same shape, but the interfaces dont have a current common inheritance
        };
    },
);

watchImmediate(
    () => addFileStore.tpsOrder,
    tpsOrder => {
        if (!tpsOrder) return;

        tpsOrder.documents?.forEach((tpsDocument: TPSDocument) => {
            fileDocumentStore.addDocument(buildAddTpsDocumentParams(tpsDocument, tpsOrder));
        });
    },
);
</script>
